import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { IconButton, Tooltip, useTheme } from "@mui/material";
import { gql } from "generated-graphql";
import { StorageLocationPickerFragment } from "generated-graphql/graphql";
import React, { useMemo, useState } from "react";
import { FieldError } from "react-hook-form";
import { useFacility } from "routes/Customer/Facility/useFacility";
import { v4 } from "uuid";

import AsyncAutocomplete from "./Forms/AsyncAutocomplete";

gql(`
  fragment StorageLocationPicker on StorageLocation {
    id
    description
    facilityId
    CA_mapNumber
    CA_gridNumber
    OR_insideOutside
    OR_storageArea
    OR_storageBuilding
    OR_storageFloor
    OR_storageQuadrant
    OR_storageRoom
    NV_insideOutside
    latitude
    longitude
  }
`);

// Coerce a primitive description into an option-like object for controlled value
const coerceDescriptionToOption = (
  description: string | undefined | null,
  facilityId: string
): StorageLocationPickerFragment | null => {
  const text = description?.trim();
  if (!text) return null;
  return {
    id: "",
    facilityId,
    description: text,
    OR_insideOutside: null,
    OR_storageArea: "",
    OR_storageBuilding: "",
    OR_storageFloor: "",
    OR_storageQuadrant: null,
    OR_storageRoom: "",
    NV_insideOutside: null,
    latitude: null,
    longitude: null,
    CA_gridNumber: null,
    CA_mapNumber: null,
  };
};

const STORAGE_LOCATIONS_QUERY = gql(`
  query StorageLocations($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    storageLocations(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...StorageLocationPicker
      }
      count
    }
  }
`);

type OptionType = StorageLocationPickerFragment & {
  inputValue?: string;
};

interface StorageLocationPickerProps {
  facilityId: string;
  onSelectLocation: (location: StorageLocationPickerFragment) => void;
  defaultSearchTerm?: string;
  disabledLocationsById?: string[];
  disabledOptionLabel?: string;
  description?: StorageLocationPickerFragment["description"];
  error?: FieldError;
  disabled?: boolean;
  disableClearable?: boolean;
  required?: boolean;
  tooltip?: React.ReactNode;
}

const initNewStorageLocation = ({
  facilityId,
  description,
  id,
  facilityLat,
  facilityLng,
}: {
  facilityId: string;
  description: string;
  id: string;
  facilityLat: number | null | undefined;
  facilityLng: number | null | undefined;
}): StorageLocationPickerFragment => ({
  id,
  facilityId,
  description: description.trim(),
  OR_insideOutside: null,
  OR_storageArea: "",
  OR_storageBuilding: "",
  OR_storageFloor: "",
  OR_storageQuadrant: null,
  OR_storageRoom: "",
  NV_insideOutside: null,
  latitude: facilityLat ?? null,
  longitude: facilityLng ?? null,
});

export const StorageLocationPicker: React.FC<StorageLocationPickerProps> = ({
  facilityId,
  onSelectLocation,
  defaultSearchTerm = "",
  description,
  error,
  disabled,
  required,
  disableClearable,
  tooltip,
}) => {
  const { data: facilityData } = useFacility(facilityId);
  const { latitude: facilityLat, longitude: facilityLng } = facilityData?.facility || {};
  const theme = useTheme();
  const [options, setOptions] = useState<StorageLocationPickerFragment[]>([]);
  const [newStorageLocation, setNewStorageLocation] =
    useState<StorageLocationPickerFragment | null>(null);

  const newLocationId = useMemo(() => v4(), []);

  // Build a controlled value from the incoming description string
  const valueOption = useMemo(
    () => coerceDescriptionToOption(description, facilityId),
    [description, facilityId]
  );

  return (
    <>
      <AsyncAutocomplete
        query={STORAGE_LOCATIONS_QUERY}
        variables={{}}
        initialSortModel={[{ field: "description", sort: "asc" }]}
        initialPageSize={25}
        disableClearable={disableClearable}
        additionalSearchString={`facilityId:${facilityId} ${defaultSearchTerm}`}
        getItems={(d) => d.storageLocations.items}
        getOptionLabel={(option: OptionType) => option.inputValue ?? option.description ?? ""}
        placeholderLabel="Storage Location"
        additionalEndAdornment={
          tooltip ? (
            <Tooltip title={tooltip}>
              <IconButton
                style={{
                  color: theme.palette.info.dark,
                  opacity: 0.8,
                }}
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          ) : null
        }
        onChange={(newValue: OptionType | null) => {
          if (!newValue) return;
          const existingStorageLocation = options.find(
            (o) => o.description === newValue?.description
          );
          onSelectLocation(
            existingStorageLocation
              ? {
                  ...existingStorageLocation,
                  latitude: existingStorageLocation.latitude ?? facilityLat,
                  longitude: existingStorageLocation.longitude ?? facilityLng,
                }
              : {
                  latitude: facilityLat,
                  longitude: facilityLng,
                  description: (newValue.description ?? "").trim(),
                  id: newValue.id,
                  facilityId: newValue.facilityId,
                }
          );
          if (!existingStorageLocation) {
            setNewStorageLocation(
              initNewStorageLocation({
                facilityId: newValue.facilityId,
                description: (newValue.description ?? "").trim(),
                id: newValue.id,
                facilityLat,
                facilityLng,
              })
            );
          }
        }}
        value={valueOption}
        error={error}
        disabled={disabled}
        isOptionEqualToValue={(o: OptionType, v: OptionType) =>
          (o.description ?? "") === (v.description ?? "")
        }
        filterOptions={(opts: OptionType[], params: { inputValue: string }) => {
          const inputValue = params.inputValue.trim();
          const filtered = opts.filter((o) =>
            (o.description ?? "").toLowerCase().includes(inputValue.toLowerCase())
          );
          const isExisting = filtered.some(
            (o) => (o.description ?? "").toLowerCase() === inputValue.toLowerCase()
          );
          if (inputValue !== "" && !isExisting) {
            filtered.push({
              facilityId,
              id: newLocationId,
              description: inputValue,
              inputValue: `Add "${inputValue}"`,
            });
          }
          return filtered;
        }}
        lazyLoad
        onOptionsUpdated={(opts) => setOptions(opts)}
        textFieldProps={{
          variant: "outlined",
          required: required,
          onBlur: (event) => {
            const input = event.target.value?.trim() ?? "";
            if (!input.length) {
              onSelectLocation(
                initNewStorageLocation({
                  facilityId,
                  description: "",
                  id: "",
                  facilityLat,
                  facilityLng,
                })
              );
              return;
            }
            const existing = options.find(
              (o) => (o.description ?? "").toLowerCase().trim() === input.toLowerCase()
            );
            const selectedNew = newStorageLocation?.description === input;
            if (!existing && !selectedNew) {
              return;
            }
            if (existing) {
              onSelectLocation({
                ...existing,
                latitude: existing.latitude ?? facilityLat,
                longitude: existing.longitude ?? facilityLng,
              });
              return;
            }
            if (selectedNew && newStorageLocation) {
              onSelectLocation({
                ...newStorageLocation,
                latitude: newStorageLocation.latitude ?? facilityLat,
                longitude: newStorageLocation.longitude ?? facilityLng,
              });
            }
          },
        }}
      />
    </>
  );
};
